import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート

const API_URL = "https://salesbreaker.jp"; // API URL はAppLayoutと同様に環境変数から取得することを検討してください

function AdminAnnouncementsPage() {
  const { user, setUser } = useOutletContext(); // ★ 2. setUserも取得（任意ですが、将来的な拡張のため）
  const [announcements, setAnnouncements] = useState([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null); // 編集中のデータ
  const [form, setForm] = useState({ title: "", content: "", is_active: true, target_role: "all" });

  const fetchAnnouncements = useCallback(async () => {
    if (!user || user.role !== "admin") {
      alert("管理者権限が必要です。");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/announcements`, {
        headers: getAuthHeader(), // ★ 3. 認証ヘッダーを追加
      });
      const data = await res.json();
      if (data.success) {
        setAnnouncements(data.announcements);
      } else {
        alert(`お知らせの取得に失敗しました: ${data.error}`);
      }
    } catch (err) {
      console.error("お知らせフェッチエラー:", err);
      alert("お知らせの取得中にエラーが発生しました。");
    }
  }, [user]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    const method = editingAnnouncement ? "PUT" : "POST";
    const url = editingAnnouncement
      ? `${API_URL}/api/admin/announcements/${editingAnnouncement.id}`
      : `${API_URL}/api/admin/announcements`;

    try {
      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 3. 認証ヘッダーを追加
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        setForm({ title: "", content: "", is_active: true, target_role: "all" });
        setEditingAnnouncement(null); // 編集モードを終了
        fetchAnnouncements(); // リストを更新
      } else {
        alert(`操作に失敗しました: ${data.error}`);
      }
    } catch (err) {
      console.error("保存エラー:", err);
      alert("保存中にエラーが発生しました。");
    }
  };

  const handleEditClick = (announcement) => {
    setEditingAnnouncement(announcement);
    setForm({
      title: announcement.title,
      content: announcement.content,
      is_active: announcement.is_active,
      target_role: announcement.target_role,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このお知らせを本当に削除しますか？")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/announcements/${id}`, {
        method: "DELETE",
        headers: {
          ...getAuthHeader(), // ★ 3. 認証ヘッダーを追加
        },
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        fetchAnnouncements();
      } else {
        alert(`削除に失敗しました: ${data.error}`);
      }
    } catch (err) {
      console.error("削除エラー:", err);
      alert("削除中にエラーが発生しました。");
    }
  };

  const handleToggleActive = async (announcement) => {
    const newIsActive = !announcement.is_active;
    if (!window.confirm(`このお知らせを${newIsActive ? '公開' : '非公開'}にしますか？`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/announcements/${announcement.id}/toggle-active`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(), // ★ 3. 認証ヘッダーを追加
        },
        body: JSON.stringify({ is_active: newIsActive }),
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        fetchAnnouncements();
      } else {
        alert(`操作に失敗しました: ${data.error}`);
      }
    } catch (err) {
      console.error("公開状態切り替えエラー:", err);
      alert("公開状態の切り替え中にエラーが発生しました。");
    }
  };

  const handleCancelEdit = () => {
    setEditingAnnouncement(null);
    setForm({ title: "", content: "", is_active: true, target_role: "all" });
  };

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-screen-xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">📢 お知らせ管理</h1>

        {/* お知らせ作成/編集フォーム */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {editingAnnouncement ? "お知らせを編集" : "新しいお知らせを作成"}
          </h2>
          <form onSubmit={handleCreateOrUpdate} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">タイトル</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleFormChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">内容</label>
              <textarea
                name="content"
                value={form.content}
                onChange={handleFormChange}
                required
                rows="5"
                className="w-full p-2 border border-gray-300 rounded-md"
              ></textarea>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleFormChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                />
                公開する
              </label>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">対象ユーザー</label>
                <select
                  name="target_role"
                  value={form.target_role}
                  onChange={handleFormChange}
                  className="p-2 border border-gray-300 rounded-md bg-white"
                >
                  <option value="all">全て</option>
                  <option value="admin">管理者</option>
                  <option value="client_admin">クライアント管理者</option>
                  <option value="staff">スタッフ</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:flex sm:space-x-3 sm:gap-0">
              <button
                type="submit"
                className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                {editingAnnouncement ? "更新" : "作成"}
              </button>
              {editingAnnouncement && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="bg-gray-400 text-white font-semibold py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
                >
                  キャンセル
                </button>
              )}
            </div>
          </form>
        </div>

        {/* お知らせ一覧 */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-800 mb-4">登録済みのお知らせ</h2>
          {announcements.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="divide-y divide-gray-100 md:hidden">
                {announcements.map((announcement) => (
                  <div key={`${announcement.id}-mobile`} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 break-words">{announcement.title}</p>
                        <p className="mt-1 text-xs text-gray-500">{new Date(announcement.published_at).toLocaleDateString("ja-JP")}</p>
                      </div>
                      <span className={`flex-shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${announcement.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {announcement.is_active ? "公開中" : "非公開"}
                      </span>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">対象: {announcement.target_role}</p>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <button onClick={() => handleEditClick(announcement)} className="rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">編集</button>
                      <button onClick={() => handleToggleActive(announcement)} className={`rounded-md px-3 py-2 text-sm font-semibold ${announcement.is_active ? "bg-red-500 text-white" : "bg-green-500 text-white"}`}>
                        {announcement.is_active ? "非公開" : "公開"}
                      </button>
                      <button onClick={() => handleDelete(announcement.id)} className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100">削除</button>
                    </div>
                  </div>
                ))}
              </div>
              <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="py-3 px-4">タイトル</th>
                    <th className="py-3 px-4">公開日</th>
                    <th className="py-3 px-4">公開状態</th>
                    <th className="py-3 px-4">対象</th>
                    <th className="py-3 px-4">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {announcements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{announcement.title}</td>
                      <td className="py-3 px-4">
                        {new Date(announcement.published_at).toLocaleDateString("ja-JP")}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            announcement.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {announcement.is_active ? "公開中" : "非公開"}
                        </span>
                      </td>
                      <td className="py-3 px-4">{announcement.target_role}</td>
                      <td className="py-3 px-4 space-x-2">
                        <button
                          onClick={() => handleEditClick(announcement)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleToggleActive(announcement)}
                          className={`text-sm py-1 px-2 rounded ${
                            announcement.is_active
                              ? "bg-red-500 text-white"
                              : "bg-green-500 text-white"
                          }`}
                        >
                          {announcement.is_active ? "非公開にする" : "公開する"}
                        </button>
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">登録済みのお知らせはありません。</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAnnouncementsPage;
